"use client";

import { useEffect } from "react";
import { captureClientError } from "@/lib/monitoring/client-capture";
import { ERROR_CODES } from "@/lib/errors/codes";

/**
 * Root SSR error handler. Fires when an error escapes every other boundary,
 * including layouts and the app's own `error.tsx`. Severity is `critical`
 * because by definition the app shell has failed.
 *
 * Routes through `captureClientError` so the `server_error` event reaches
 * the same PostHog → Slack destination used by the API middleware catch.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    captureClientError(error, {
      severity: "critical",
      category: "REACT",
      errorCode: ERROR_CODES.SSR_RENDER_FAILED,
      operation: "global-error.tsx",
    });
  }, [error]);

  return (
    <html>
      <body>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
          <div className="max-w-md w-full text-center space-y-4">
            <h1 className="text-2xl font-bold text-gray-900">
              Something went wrong
            </h1>
            <p className="text-gray-600">
              An unexpected error occurred. Please try refreshing the page.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => reset()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => (window.location.href = "/")}
                className="px-4 py-2 bg-gray-200 text-gray-900 rounded-md hover:bg-gray-300 transition-colors"
              >
                Go Home
              </button>
            </div>
          </div>
        </div>
      </body>
    </html>
  );
}
